import { DOCUMENT } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    Inject,
    OnInit,
} from '@angular/core';
import { environment } from '@environment';
import { AuthService, RegisterSvgAsIconService } from '@shared/services';
import { Router } from '@angular/router';
import {
    NgcCookieConsentService,
    NgcInitializationErrorEvent,
    NgcInitializingEvent,
    NgcNoCookieLawEvent,
    NgcStatusChangeEvent,
} from 'ngx-cookieconsent';
import { of, Subscription, throwError } from 'rxjs';
import { ELocalStorageKeys } from '@core/models/localStorage.model';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
    isLogged = false;
    token!: string;

    private popupOpenSubscription!: Subscription;
    private popupCloseSubscription!: Subscription;
    private initializingSubscription!: Subscription;
    private initializedSubscription!: Subscription;
    private initializationErrorSubscription!: Subscription;
    private statusChangeSubscription!: Subscription;
    private revokeChoiceSubscription!: Subscription;
    private noCookieLawSubscription!: Subscription;

    constructor(
        @Inject(DOCUMENT) document: any,
        private _router: Router,
        private _registerSvgAsIcon: RegisterSvgAsIconService,
        private cookieService: NgcCookieConsentService,
        public authService: AuthService
    ) {
        const tenant = this.authService.getTenant()
        if (tenant !== '') {
          const urlTree = this._router.parseUrl(document.location.pathname);
          const primarySegments = urlTree.root.children['primary']?.segments ?? [];
      
          const urlParams = new URLSearchParams(window.location.search);
          const register_ok = urlParams.get('access-token');
          const originFrom = urlParams.get('originFrom');
      
          this.authService.setTenant(tenant);
          environment.api_url = environment.routes[tenant];
      
          let path: string[];
      
          if (tenant !== 'register' || (tenant === 'register' && !register_ok)) {
            // Si ya hay segmentos después de /page/:tenant, los respetamos (ej: fleet/vehicles/profile/123)
            if (primarySegments.length > 2) {
              path = primarySegments.map((s) => s.path);
            } else {
              // Sin ruta → vamos a dashboard
              path = ['page', tenant, 'dashboard'];
            }
          } else if (tenant === 'register' && register_ok) {
            path = ['thankyou'];
          } else {
            path = ['page', tenant, 'dashboard'];
          }
      
          this._router.navigate(path, { queryParams: { originFrom } });
        } else {
          this._router.navigate(['notfound']);
        }
      }

    ngOnInit(): void {
        const urlParams = new URLSearchParams(window.location.search);

        let token = localStorage.getItem(ELocalStorageKeys.accessToken);
        let refresh_token = localStorage.getItem(
            ELocalStorageKeys.refreshToken
        );
        let tenant = this.authService.getTenant();

        if (!tenant) tenant = 'mex';

        if (!token) {
            token = urlParams.get('access-token');
            refresh_token = urlParams.get('refresh-token')!;
        }
        const redirectTarget = `${environment.app_base_url}/page/${tenant}`;
        const registerUrl = `${
            environment.gateway
        }/login?redirect_uri=${encodeURIComponent(redirectTarget)}`;

        if (!token) {
            window.location.replace(registerUrl);
        } else {
            if (this.authService.isTokenExpired(token)) {
                this.authService.refreshToken(refresh_token!).subscribe(
                    (data: any) => {
                        token = data.token;
                        refresh_token = data.refreshToken;
                        localStorage.setItem(
                            ELocalStorageKeys.accessToken,
                            token!
                        );
                        localStorage.setItem(
                            ELocalStorageKeys.refreshToken,
                            refresh_token!
                        );
                        this.authService.initAppRoles();
                        return of(token);
                    },
                    (err) => {
                        if (err.status === 400) {
                            localStorage.clear();
                            sessionStorage.clear();
                            window.location.href = registerUrl;
                        }
                        return throwError(() => err);
                    }
                );
            }

            if (!this.authService.canUserAccessApp(token, tenant)) {
                this.isLogged = true;
                this._router.navigate(['/unauthorized']);
            } else {
                this.isLogged = true;

                localStorage.setItem(ELocalStorageKeys.accessToken, token);
                localStorage.setItem(
                    ELocalStorageKeys.refreshToken,
                    refresh_token!
                );
                this.authService.initAppRoles();
            }
        }
        // subscribe to cookieconsent observables to react to main events
        this.popupOpenSubscription = this.cookieService.popupOpen$.subscribe(
            () => {
                // you can use this.cookieService.getConfig() to do stuff...
            }
        );

        this.popupCloseSubscription = this.cookieService.popupClose$.subscribe(
            () => {
                // you can use this.cookieService.getConfig() to do stuff...
            }
        );

        this.initializingSubscription =
            this.cookieService.initializing$.subscribe(
                (event: NgcInitializingEvent) => {
                    // the cookieconsent is initilializing... Not yet safe to call methods like `NgcCookieConsentService.hasAnswered()`
                    console.log(`initializing: ${JSON.stringify(event)}`);
                }
            );

        this.initializedSubscription =
            this.cookieService.initialized$.subscribe(() => {
                // the cookieconsent has been successfully initialized.
                // It's now safe to use methods on NgcCookieConsentService that require it, like `hasAnswered()` for eg...
                console.log(`initialized: ${JSON.stringify(event)}`);
            });

        this.initializationErrorSubscription =
            this.cookieService.initializationError$.subscribe(
                (event: NgcInitializationErrorEvent) => {
                    // the cookieconsent has failed to initialize...
                    console.log(
                        `initializationError: ${JSON.stringify(
                            event.error?.message
                        )}`
                    );
                }
            );

        this.statusChangeSubscription =
            this.cookieService.statusChange$.subscribe(
                (event: NgcStatusChangeEvent) => {
                    // you can use this.cookieService.getConfig() to do stuff...
                }
            );

        this.revokeChoiceSubscription =
            this.cookieService.revokeChoice$.subscribe(() => {
                // you can use this.cookieService.getConfig() to do stuff...
            });

        this.noCookieLawSubscription =
            this.cookieService.noCookieLaw$.subscribe(
                (event: NgcNoCookieLawEvent) => {
                    // you can use this.cookieService.getConfig() to do stuff...
                }
            );
    }

    ngOnDestroy() {
        this.popupOpenSubscription.unsubscribe();
        this.popupCloseSubscription.unsubscribe();
        this.initializingSubscription.unsubscribe();
        this.initializedSubscription.unsubscribe();
        this.initializationErrorSubscription.unsubscribe();
        this.statusChangeSubscription.unsubscribe();
        this.revokeChoiceSubscription.unsubscribe();
        this.noCookieLawSubscription.unsubscribe();
    }
}
